/**
 * Helpers for the host-config personal-computer attachment, shared by every
 * editor surface (ClientConfigEditor, BehaviorTab) so the "computer ↔
 * computer-backed tool" invariant is enforced identically everywhere.
 *
 * The invariant (also enforced backend-side in `ensureHostConfigV2`): a
 * computer-backed built-in tool (catalog `requiresComputer`, e.g. `bash`) is
 * only valid when the host also attaches a `computer` resource.
 */
import type { BuiltInToolCatalogEntry } from "@/hooks/useBuiltInToolCatalog";
import type { HostConfigInputV2 } from "@/lib/client-config-v2";

/**
 * Built-in tool ids the inspector implements as computer-backed. The catalog's
 * `requiresComputer` flag is authoritative for AVAILABILITY (which tools the
 * deployment exposes), but it can be `undefined` while loading and OMITS
 * disabled rows — and `bash` ships disabled until launch. So the CLEANUP paths
 * (detach, eval-suite sanitize) union this known floor with the catalog, to
 * guarantee a computer-dependent id never survives without its resource even
 * when the catalog can't identify it. Mirrors the server resolver's hardcoded
 * `BASH_TOOL_NAME` (server/utils/built-in-tools/registry.ts). This floor is
 * NOT used to decide whether to offer attaching a computer — see
 * `catalogHasComputerBackedTool`, which stays catalog-only so a disabled tool
 * never resurrects a dead pre-launch toggle.
 */
const KNOWN_COMPUTER_BACKED_TOOL_IDS: readonly string[] = ["bash"];

/**
 * Whether the catalog currently exposes a computer-backed tool. Catalog-only
 * (no floor) — this gates OFFERING a computer attachment, which must follow
 * what the deployment has actually enabled.
 */
export function catalogHasComputerBackedTool(
  catalog: ReadonlyArray<BuiltInToolCatalogEntry> | undefined
): boolean {
  return (catalog ?? []).some((t) => t.requiresComputer);
}

/**
 * The set of computer-backed built-in tool ids for CLEANUP purposes: the
 * catalog's `requiresComputer` ids unioned with the known floor, so detaching
 * a computer always strips e.g. `bash` regardless of catalog load state or a
 * disabled row.
 */
export function computerBackedToolIds(
  catalog: ReadonlyArray<BuiltInToolCatalogEntry> | undefined
): Set<string> {
  const ids = new Set<string>(KNOWN_COMPUTER_BACKED_TOOL_IDS);
  for (const tool of catalog ?? []) {
    if (tool.requiresComputer) ids.add(tool.id);
  }
  return ids;
}

/**
 * The catalog rows an editor should actually RENDER, honoring the
 * `computers-enabled` rollout flag: when the flag is off for this user,
 * computer-backed rows are hidden — the backend `bash` row can be enabled
 * deployment-wide without leaking a dead "requires a computer" checkbox to
 * users who can't see the (flag-gated) computer toggle it points at. A row
 * whose id is ALREADY selected stays visible regardless, preserving the
 * checkbox list's invariant that a stale selected id is always removable.
 * `undefined` passes through (callers treat it as "catalog still loading").
 */
export function visibleBuiltInToolCatalog(
  catalog: ReadonlyArray<BuiltInToolCatalogEntry> | undefined,
  opts: {
    computersEnabled: boolean;
    selectedIds: ReadonlyArray<string>;
  }
): ReadonlyArray<BuiltInToolCatalogEntry> | undefined {
  if (catalog === undefined || opts.computersEnabled) return catalog;
  const selected = new Set(opts.selectedIds);
  return catalog.filter((t) => !t.requiresComputer || selected.has(t.id));
}

/** Patch that attaches a personal computer (the only MVP resource shape). */
export function attachComputerPatch(): Partial<HostConfigInputV2> {
  return { computer: { kind: "personal" } };
}

/**
 * The box home — the default working directory when `computer.workdir` is unset.
 * Chosen so COMP-14 chat attachments (which land in `/home/user/attachments`)
 * are reachable by a plain relative path from the default cwd. Mirrors the
 * server's `HOME_ROOT` (`server/utils/computers/path-confine.ts`).
 */
export const DEFAULT_COMPUTER_WORKDIR = "/home/user";

/**
 * Client-side mirror of the server's `resolveWorkingDirectory` confinement
 * (COMP-16), for inline field validation only — the server check is
 * authoritative. Returns an error string, or `null` when acceptable (a blank
 * value is acceptable and means "use the default"). Keep in lockstep with
 * `server/utils/computers/path-confine.ts`.
 */
export function validateComputerWorkdir(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null; // blank ⇒ default /home/user
  if (!trimmed.startsWith("/")) {
    return "Use an absolute path under /home/user.";
  }
  const normalized = trimmed.replace(/\/+$/, "");
  if (
    normalized !== DEFAULT_COMPUTER_WORKDIR &&
    !normalized.startsWith(`${DEFAULT_COMPUTER_WORKDIR}/`)
  ) {
    return "Must resolve under /home/user.";
  }
  if (normalized.split("/").includes("..")) {
    return 'No ".." segments.';
  }
  return null;
}

/**
 * Patch that sets (or clears) the computer's working directory (COMP-16). A
 * blank value OR the default `/home/user` CLEARS `workdir`, so the default
 * hashes identically to "never set" (content-addressed host configs: the box
 * default is `/home/user` either way, so storing it would only fork the row).
 * No-op when no computer is attached.
 */
export function setComputerWorkdirPatch(
  value: HostConfigInputV2,
  workdir: string
): Partial<HostConfigInputV2> {
  if (value.computer === undefined) return {};
  const trimmed = workdir.trim().replace(/\/+$/, "");
  const keep = trimmed !== "" && trimmed !== DEFAULT_COMPUTER_WORKDIR;
  return {
    computer: {
      ...value.computer,
      workdir: keep ? trimmed : undefined,
    },
  };
}

/**
 * Whether the editor should render the personal-computer toggle. Shown when
 * the catalog exposes a computer-backed tool (so the `bash` row stays hidden
 * until launch) OR when a computer is already attached — so an existing
 * attachment is always DETACHABLE even if no computer-backed tool is currently
 * in the catalog. Never on surfaces that disallow computers (eval suites).
 */
export function shouldShowComputerToggle(opts: {
  catalogHasComputerBackedTool: boolean;
  computerAttached: boolean;
  disallowed?: boolean;
}): boolean {
  if (opts.disallowed) return false;
  return opts.catalogHasComputerBackedTool || opts.computerAttached;
}

/**
 * Patch that detaches the computer AND drops any computer-backed tool ids, so
 * the resulting draft can't fail the backend's requiresComputer invariant on
 * save (detaching the resource must take its dependent capabilities with it).
 *
 * Also clears `harness`: the Claude Code harness runs inside the computer, so a
 * harness without a computer would fail the backend's `harness ⇒ computer`
 * invariant. Detaching the resource takes the runtime that depends on it too.
 */
export function detachComputerPatch(
  value: HostConfigInputV2,
  catalog: ReadonlyArray<BuiltInToolCatalogEntry> | undefined
): Partial<HostConfigInputV2> {
  const backed = computerBackedToolIds(catalog);
  return {
    computer: undefined,
    harness: undefined,
    builtInToolIds: value.builtInToolIds.filter((id) => !backed.has(id)),
  };
}

/**
 * Sanitize a host config for an eval suite: clear the `computer` resource and
 * strip computer-backed tool ids. Eval runs are aborted by the backend when
 * the resolved host config carries a computer (a personal computer is mutable
 * per-user state an eval can't reproduce), so the eval-suite editor must never
 * persist one — including via "Reset to project default", which copies a
 * project config that may have a computer attached, and on first load of a
 * pre-existing suite config. Returns the SAME reference when already clean so
 * it never introduces spurious dirty state. The `computer` clear is
 * catalog-independent (the part the backend guard keys on); id-stripping is
 * best-effort with whatever catalog has loaded.
 */
export function sanitizeHostConfigForEvalSuite(
  value: HostConfigInputV2,
  catalog: ReadonlyArray<BuiltInToolCatalogEntry> | undefined
): HostConfigInputV2 {
  const backed = computerBackedToolIds(catalog);
  const cleanedIds = value.builtInToolIds.filter((id) => !backed.has(id));
  const idsChanged = cleanedIds.length !== value.builtInToolIds.length;
  // Clear `harness` too: the Claude Code harness runs inside the computer, so a
  // harness left set with no computer violates the backend `harness ⇒ computer`
  // invariant and would route an eval run toward the real harness path. Mirrors
  // detachComputerPatch — dropping the computer takes its dependent runtime.
  const harnessChanged = value.harness !== undefined;
  if (value.computer === undefined && !idsChanged && !harnessChanged) {
    return value;
  }
  return {
    ...value,
    computer: undefined,
    harness: undefined,
    builtInToolIds: cleanedIds,
  };
}
